/**
 * Per-tab history helpers for Basecamp extension opens.
 * Every limb object open must record enough metadata for Back/Forward restore.
 */

import type { OpenTab } from '@/components/features/document-tabs'
import type { NavigationEntry } from '@/hooks/use-navigation-history'
import {
  loadProjectsPanelUiState,
  saveProjectsPanelUiState,
} from '@/lib/basecamp-project-relevance'
import { parseBasecampTabId } from '@/lib/basecamp-tab-routing'

export type IntegrationNavFields = Omit<NavigationEntry, 'timestamp'>

export function isProjectsIndexTab(tab: Pick<OpenTab, 'id' | 'integrationData'>): boolean {
  return tab.id === 'basecamp-projects' || tab.integrationData?.section === 'projects'
}

export function isProjectsIndexDocumentId(documentId: string | null | undefined): boolean {
  if (!documentId) return false
  if (documentId === 'basecamp-projects') return true
  const parsed = parseBasecampTabId(documentId)
  return parsed?.kind === 'static' && parsed.tabId === 'basecamp-projects'
}

/** Build a history entry from an integration-view-change detail (or equivalent). */
export function navigationFieldsFromIntegrationOpen(args: {
  tabId: string
  type?: string
  integrationData?: OpenTab['integrationData'] | null
}): IntegrationNavFields {
  const { tabId, type, integrationData } = args
  const parsed = parseBasecampTabId(tabId)
  const projectId =
    integrationData?.projectId ??
    (parsed?.kind === 'project' ? parsed.projectId : undefined)
  const listId =
    integrationData?.listId ??
    (parsed?.kind === 'todo-list' ? parsed.listId : undefined)
  const todoId =
    integrationData?.todoId ??
    (parsed?.kind === 'todo' ? parsed.todoId : undefined)

  return {
    documentId: tabId,
    viewMode: 'single',
    ...(type ? { tabType: type } : {}),
    ...(projectId ? { projectId } : {}),
    ...(listId ? { listId } : {}),
    ...(todoId ? { todoId } : {}),
    ...(integrationData?.organizationId
      ? { organizationId: integrationData.organizationId }
      : {}),
  }
}

/** Build a history entry from the currently open tab (seed before replace). */
export function navigationFieldsFromOpenTab(tab: OpenTab): IntegrationNavFields {
  const fields = navigationFieldsFromIntegrationOpen({
    tabId: tab.id,
    type: tab.type,
    integrationData: tab.integrationData,
  })
  const withTitle = tab.title ? { ...fields, title: tab.title } : fields
  if (!isProjectsIndexTab(tab)) return withTitle
  const ui = loadProjectsPanelUiState(tab.integrationData?.organizationId)
  return {
    ...withTitle,
    ...(ui.selectedId
      ? { selectedEntityId: ui.selectedId, projectId: ui.selectedId }
      : {}),
    previewOpen: ui.previewOpen,
  }
}

/**
 * Rehydrate Projects list selection + preview pin from a history entry.
 * Call when Back/Forward restores the Projects index - not a project home tab.
 */
export function applyProjectsIndexHistoryState(
  entry: Pick<
    NavigationEntry,
    'documentId' | 'selectedEntityId' | 'projectId' | 'previewOpen' | 'organizationId'
  >,
): void {
  if (!isProjectsIndexDocumentId(entry.documentId)) return
  const selectedId = entry.selectedEntityId ?? entry.projectId ?? null
  if (!selectedId) return
  saveProjectsPanelUiState(entry.organizationId, {
    selectedId,
    previewOpen: entry.previewOpen !== false,
  })
}
